import React, { FC, useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { BemMethod, createBem, PlatformTypes } from '@om-fe/utils';
import { AppState } from '@om-fe/core/core/types';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { MixedGroupedTournament } from '../components/mixed-grouped/containers/mixed-grouped-tournament/mixed-grouped-tournament';
import { Breakpoints } from '@om-fe/core/core/store/breakpoints';
import { getMainMarketsLimit } from '@om-fe/core/components/match-list/utils/match-list.utils';
import { withSelectionBetslipData, WithSelectionBetslipDataModel } from '@om-fe/widgets/dist/widgets/betslip/with-betslip-selection-data/with-betslip-selection-data';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { DeepLink } from '@om-fe/core/utils/deep-link';
import { TournamentParams } from '@om-fe/models';
import { getVirtualStateExtraParams, setVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';

interface TournamentEsportPageStoreProps {
    readonly isLarge: boolean;
    readonly breakpoints?: Breakpoints;
    readonly deviceType?: PlatformTypes;
}

type TournamentEsportPagePublicProps = RouteComponentProps<{
    readonly disciplineId: string;
    readonly disciplineName: string;
    readonly tournamentName: string;
    readonly tournamentId: string;
    readonly eventCategoryId?: string;
    readonly eventCategoryName?: string;
    readonly locationName?: string;
    readonly locationId?: string;
    readonly activeTab?: string;
    readonly bettingOfferIds?: string;
    readonly stake?: string;
}> & WithSelectionBetslipDataModel;

type TournamentEsportPageProps = TournamentEsportPageStoreProps & TournamentEsportPagePublicProps;

const bem: BemMethod = createBem('TournamentPage');

function TournamentEsportPageComponent(
    {
        isLarge,
        match: { params },
        breakpoints,
        deviceType,
        addSelection,
        setSingleSelectionStake,
        setMasterStake,
    }: TournamentEsportPageProps,
) {
    const [tournamentParams, setTournamentParams] = useState<TournamentParams>(params);
    let sportPageType: DisciplineRouteType;
    const { 
        eventCategoryId,
        locationId,
        disciplineId,
        tournamentId,
        activeTab,
    } = tournamentParams;
    const storage: TournamentParams = getVirtualStateExtraParams<TournamentParams>();
    const mainMarketsLimit: number = getMainMarketsLimit(breakpoints);
    const getTournamentParams: TournamentParams = typeof storage?.tournamentId !== 'undefined' ? storage : tournamentParams;
    const handleSetTournamentParams = useCallback((data: TournamentParams) => {
        setTournamentParams(data);
        setVirtualStateExtraParams(data);
    }, []);

    useEffect(() => {
        const { tournamentId, bettingOfferIds, stake } = params;
        if (params) {
            setTournamentParams(params);
        }

        DeepLink({ bettingOfferIds, stake, id: tournamentId, addSelection, setSingleSelectionStake, setMasterStake });
    }, [params, DeepLink ]);

    if (eventCategoryId) {
        sportPageType = DisciplineRouteType.Category;
    } else if (locationId) {
        sportPageType = DisciplineRouteType.Location;
    } else {
        sportPageType = DisciplineRouteType.Parent;
    }

    return (
        <div className={bem()}>
            <Breadcrumbs
                disciplineId={disciplineId}
                locationId={locationId}
                tournamentId={tournamentId !== '0' ? tournamentId : undefined}
                categoryId={eventCategoryId}
                isLarge={isLarge}
                routeType={sportPageType}
            />
            <UserBehaviorTrackingContext.Provider value={{ userBehaviorTrackingPage: RouteNames.tournamentLocation, userBehaviorTrackingLocation: UserBehaviorTrackingLocation.GroupedMatchList }}>
                <MixedGroupedTournament
                    disciplineId={disciplineId}
                    locationId={locationId}
                    tournamentId={tournamentId}
                    sportPageType={sportPageType}
                    eventCategoryId={eventCategoryId}
                    activeTab={activeTab}
                    mainMarketsLimit={mainMarketsLimit}
                    deviceType={deviceType}
                    handleTournamentParams={handleSetTournamentParams}
                    tournamentParams={getTournamentParams}
                />
            </UserBehaviorTrackingContext.Provider>
        </div>
    );
}

export const TournamentEsportPage: FC<TournamentEsportPagePublicProps> = compose (
    withSelectionBetslipData<TournamentEsportPagePublicProps>(),
    connect<TournamentEsportPageStoreProps, undefined, TournamentEsportPagePublicProps>(
        (state: AppState) => {
            return {
                isLarge: state.breakpoints.large || state.breakpoints.xl,
                breakpoints: state.breakpoints,
                deviceType: state.deviceInfo?.platform.type,
            };
        }
    ),
)(TournamentEsportPageComponent);